import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  FilePlus2,
  FileText,
  FolderOpen,
  Plus,
  Search,
  Settings,
  Trash2,
} from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useMatterLocale } from '@/components/contexts/MatterJurisdictionContext';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { DraftDocxViewer } from '@/components/drafts/DraftDocxViewer';
import { NewDraftDialog } from '@/components/drafts/NewDraftDialog';
import { TemplateUploadDropArea } from '@/components/drafts/TemplateUploadDropArea';
import {
  isTemplateMetadataValid,
  templateDescriptionLength,
  TEMPLATE_DESCRIPTION_MAX_LENGTH,
  templateMatcherPromptLength,
  TEMPLATE_MATCHER_PROMPT_MIN_LENGTH,
} from '@/components/drafts/templateValidation';
import PageError from '@/components/shared/Error';
import { Loading } from '@/components/shared';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { FileTypeCell } from '@/components/ui/column-builders';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BulkActionBar } from '@/components/ui/bulk-action-bar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileWordIcon } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import { Button as TableIconButton } from '@/components/ui/button';
import { Button } from '@/components/ui/NewButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  fileTypeFacetOptionsForValues,
  SimpleTableFacetedFilter,
} from '@/components/ui/simple-table-faceted-filter';
import { SimpleTableActionsDropdown } from '@/components/ui/simple-table-actions-dropdown';
import { Textarea } from '@/components/ui/textarea';
import {
  createDraftDocumentTemplate,
  deleteDraftDocument,
  deleteDraftDocumentTemplate,
  type DraftDocument,
  type DraftDocumentTemplate,
  type DraftDocumentVisibility,
  downloadDraftDocument,
  downloadDraftDocumentTemplate,
  updateDraftDocumentTemplate,
  updateDraftDocumentVisibility,
  useDraftDocuments,
  useDraftDocumentTemplateDocx,
  useDraftDocumentTemplates,
} from '@/hooks/useDraftDocuments';
import {
  DraftVisibilityMenu,
  draftVisibilityLabel,
} from '@/drafting/components/DraftVisibilityStatus';
import { formatDateTime } from '@/drafting/utils/history';
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import useMatter from '@/hooks/useMatter';
import { apiErrorMessage } from '@/lib/api-client';
import { cn } from '@/lib/utils';

const draftUpdatedAt = (draftDocument: DraftDocument) =>
  draftDocument.updated_at ?? draftDocument.created_at;

type DraftSortField = 'title' | 'updated';
type DraftSortDirection = 'asc' | 'desc';
type DraftWorkspaceTab = 'drafts' | 'templates';
type TemplateScopeFilter = 'all' | 'system' | 'tenant' | 'matter' | 'user';

interface DraftSort {
  field: DraftSortField;
  direction: DraftSortDirection;
}

const templateScopeLabel = (scope: string) => {
  if (scope === 'system') return 'Crimson';
  if (scope === 'tenant') return 'Tenant';
  if (scope === 'matter') return 'Matter';
  if (scope === 'user') return 'Mine';
  return scope;
};

const isTenantLevelTemplate = (template: Pick<DraftDocumentTemplate, 'scope'>) =>
  template.scope === 'tenant';

const canManageTemplateFromMatterView = (template: DraftDocumentTemplate) =>
  template.can_manage && !isTenantLevelTemplate(template);

const unavailableMatterTemplateActionTooltip = (
  template: DraftDocumentTemplate,
  action: 'configure' | 'delete',
) => {
  if (isTenantLevelTemplate(template)) {
    const actionLabel = action === 'configure' ? 'configured' : 'deleted';
    return `Tenant templates can only be ${actionLabel} from Admin`;
  }
  return `Only the managing owner can ${action} this template`;
};

const templateScopeFilterOptions: Array<{ value: TemplateScopeFilter; label: string }> = [
  { value: 'all', label: 'All scopes' },
  { value: 'system', label: 'Crimson' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'matter', label: 'Matter' },
  { value: 'user', label: 'Mine' },
];

const templateScopeFacetOptionsForTemplates = (
  templates: DraftDocumentTemplate[],
  currentValue: TemplateScopeFilter,
) => {
  const availableScopes = new Set(templates.map((template) => template.scope));
  if (currentValue !== 'all') {
    availableScopes.add(currentValue);
  }

  return templateScopeFilterOptions.filter((option) => (
    option.value !== 'all' && availableScopes.has(option.value)
  ));
};

const draftOwnerLabel = (draftDocument: DraftDocument) => {
  if (draftDocument.is_owner) return 'You';
  if (!draftDocument.owner && draftDocument.file_reference?.source !== 'crimson') return 'Synced';
  return draftDocument.owner?.name || draftDocument.owner?.email || 'Another member';
};

const draftLastUpdatedByLabel = (
  draftDocument: DraftDocument,
  currentUser?: { email?: string | null; guid?: string | null; user_id?: number | null } | null,
) => {
  const actor = draftDocument.last_updated_by ?? draftDocument.owner;
  if (!actor && draftDocument.file_reference?.source !== 'crimson') return 'Synced';
  if (!actor) return 'Unknown';
  const actorEmail = actor.email?.toLowerCase();
  const currentEmail = currentUser?.email?.toLowerCase();
  if (
    (actor.user_guid && currentUser?.guid && actor.user_guid === currentUser.guid)
    || (actor.user_id && currentUser?.user_id && actor.user_id === currentUser.user_id)
    || (actorEmail && currentEmail && actorEmail === currentEmail)
  ) {
    return 'You';
  }
  return actor.name || actor.email || 'Another member';
};

const draftOwnerFacetOptionsForDrafts = (drafts: DraftDocument[]) => (
  Array.from(new Set(drafts.map(draftOwnerLabel)))
    .sort((a, b) => {
      if (a === 'You') return -1;
      if (b === 'You') return 1;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    })
    .map((owner) => ({ label: owner, value: owner }))
);

const templateDescription = (template: DraftDocumentTemplate) =>
  template.description || template.when_to_use || 'No description';

const titleFromTemplateFile = (fileName: string) =>
  fileName.replace(/\.docx$/i, '').replace(/[^\w .-]+/g, ' ').replace(/\s+/g, ' ').trim();

const sortDrafts = (drafts: DraftDocument[], sort: DraftSort) => (
  [...drafts].sort((a, b) => {
    if (sort.field === 'title') {
      const result = a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
      return sort.direction === 'asc' ? result : -result;
    }

    const aTime = new Date(draftUpdatedAt(a)).getTime();
    const bTime = new Date(draftUpdatedAt(b)).getTime();
    const result = aTime - bTime;
    return sort.direction === 'asc' ? result : -result;
  })
);

const draftFolderLabel = (draftDocument: DraftDocument) => {
  const folderName = draftDocument.file_reference?.folder_name?.trim();
  if (!folderName) return null;
  return `In ${folderName}`;
};

const draftSubtitle = (draftDocument: DraftDocument) => {
  const folderLabel = draftFolderLabel(draftDocument);
  if (folderLabel) return folderLabel;
  if (draftDocument.read_only) {
    return draftDocument.read_only_reason || 'Read-only synced draft';
  }
  const templateTitle = draftDocument.created_from_template?.title?.trim();
  if (templateTitle) return `From ${templateTitle}`;
  if (draftDocument.file_reference?.source && draftDocument.file_reference.source !== 'crimson') {
    return `From ${draftDocument.file_reference.display_name}`;
  }
  if (draftDocument.file_reference) {
    return 'Saved in case files';
  }
  return 'Drafting workspace';
};

const DRAFT_TEMPLATE_FORMAT = 'DOCX';
const DRAFT_TEMPLATE_FILE_TYPE = 'Word';
const DRAFT_WORKSPACE_DEFAULT_PAGE_SIZE = 250;
const DRAFT_WORKSPACE_PAGE_SIZE_OPTIONS = [25, 50, 100, 250];
const CREATE_DRAFT_BUTTON_CLASS_NAME =
  'h-9 rounded-lg !bg-primary px-3 text-sm font-medium text-white !shadow-none hover:!bg-primary-900 hover:text-white [&_svg]:size-4';

const draftDocumentFormat = (draftDocument: DraftDocument) => (
  (draftDocument.file_extension || '')
    .replace(/^\./, '')
    .toUpperCase() || 'FILE'
);

const draftDocumentFileType = (draftDocument: DraftDocument) => {
  const extension = (draftDocument.file_extension || '').toLowerCase();
  if (extension === '.docx' || extension === '.doc') return 'Word';
  if (extension === '.pdf') return 'PDF';
  if (['.xlsx', '.xlsm', '.xls', '.csv', '.tsv'].includes(extension)) return 'Excel';
  if (['.eml', '.msg'].includes(extension)) return 'Email';
  return draftDocumentFormat(draftDocument);
};

const draftDocumentFileName = (draftDocument: DraftDocument) => (
  draftDocument.file_name || draftDocument.file_reference?.display_name || draftDocument.title || 'draft'
);

const draftSearchText = (draftDocument: DraftDocument) => [
  draftDocument.title,
  draftSubtitle(draftDocument),
  draftOwnerLabel(draftDocument),
  draftVisibilityLabel(draftDocument.visibility),
  draftDocumentFormat(draftDocument),
  draftDocumentFileType(draftDocument),
  draftDocument.file_name,
  draftDocument.file_reference?.display_name,
  draftDocument.file_reference?.folder_name,
  draftDocument.file_reference?.folder_path,
  draftDocument.file_reference?.source,
].filter(Boolean).join(' ').toLowerCase();

const DraftSortHeader = ({
  activeSort,
  children,
  field,
  onClick,
}: {
  activeSort: DraftSort;
  children: React.ReactNode;
  field: DraftSortField;
  onClick: (field: DraftSortField) => void;
}) => {
  return (
    <div className="flex flex-row items-center gap-0">
      <span>{children}</span>
      <TableIconButton
        type="button"
        variant="ghost"
        size="sm"
        className="ml-2 h-8 w-6"
        onClick={() => onClick(field)}
        aria-pressed={activeSort.field === field}
      >
        <ArrowUpDown className="h-4 w-4" />
      </TableIconButton>
    </div>
  );
};

const DraftOwnerFilterHeader = ({
  options,
  selectedValues,
  onSelectedValuesChange,
}: {
  options: Array<{ label: string; value: string }>;
  selectedValues: string[];
  onSelectedValuesChange: (values: string[]) => void;
}) => (
  <div className="flex flex-row items-center gap-0">
    <span>Owner</span>
    <div className="ml-2">
      <SimpleTableFacetedFilter
        title="Owner"
        options={options}
        selectedValues={selectedValues}
        onSelectedValuesChange={onSelectedValuesChange}
      />
    </div>
  </div>
);

const DraftFileTypeHeader = ({
  options,
  selectedValues,
  onSelectedValuesChange,
}: {
  options: Array<{ label: string; value: string }>;
  selectedValues: string[];
  onSelectedValuesChange: (values: string[]) => void;
}) => (
  <div className="flex justify-center">
    <SimpleTableFacetedFilter
      title="File type"
      options={options}
      selectedValues={selectedValues}
      onSelectedValuesChange={onSelectedValuesChange}
    />
  </div>
);

const TemplateScopeFilterHeader = ({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: TemplateScopeFilter; label: string }>;
  value: TemplateScopeFilter;
  onChange: (value: TemplateScopeFilter) => void;
}) => {
  const selectedValues = value === 'all' ? [] : [value];

  return (
    <div className="flex flex-row items-center gap-0">
      <span>Scope</span>
      <div className="ml-2">
        <SimpleTableFacetedFilter
          title="Scope"
          options={options}
          selectedValues={selectedValues}
          onSelectedValuesChange={(values) => onChange((values.at(-1) as TemplateScopeFilter | undefined) ?? 'all')}
        />
      </div>
    </div>
  );
};

interface DraftWorkspaceTableColumn<T> {
  key: string;
  header: React.ReactNode;
  headerClassName?: string;
  cellClassName?: string;
  width?: string;
  render: (row: T) => React.ReactNode;
}

const DraftWorkspaceTable = <T,>({
  columns,
  emptyAction,
  emptyIcon,
  emptyTitle,
  getCanSelectRow,
  getRowKey,
  initialPageSize = DRAFT_WORKSPACE_DEFAULT_PAGE_SIZE,
  onRowClick,
  onSelectedRowKeysChange,
  pageSizeOptions = DRAFT_WORKSPACE_PAGE_SIZE_OPTIONS,
  renderBulkActions,
  rows,
  selectedRowKeys,
  tableMinWidth = '760px',
  tableWidth = '100%',
  totalLabel,
}: {
  columns: Array<DraftWorkspaceTableColumn<T>>;
  emptyAction?: React.ReactNode;
  emptyIcon: React.ReactNode;
  emptyTitle: string;
  getCanSelectRow?: (row: T) => boolean;
  getRowKey: (row: T) => string;
  initialPageSize?: number;
  onRowClick?: (row: T) => void;
  onSelectedRowKeysChange?: (keys: Set<string>) => void;
  pageSizeOptions?: number[];
  renderBulkActions?: (params: {
    clearSelection: () => void;
    selectedCount: number;
    selectedRows: T[];
  }) => React.ReactNode;
  rows: T[];
  selectedRowKeys?: Set<string>;
  tableMinWidth?: string;
  tableWidth?: string;
  totalLabel: string;
}) => {
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(initialPageSize);
  const previousPaginationInputs = React.useRef({ pageSize, rows });
  const selectionEnabled = Boolean(selectedRowKeys && onSelectedRowKeysChange);
  const canSelectRow = React.useCallback((row: T) => (
    getCanSelectRow ? getCanSelectRow(row) : true
  ), [getCanSelectRow]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const firstPageIndex = 0;
  const lastPageIndex = pageCount - 1;
  const pageStartIndex = pageIndex * pageSize;
  const pageRows = React.useMemo(
    () => rows.slice(pageStartIndex, pageStartIndex + pageSize),
    [pageSize, pageStartIndex, rows],
  );
  const selectedRows = React.useMemo(() => (
    selectedRowKeys
      ? rows.filter((row) => selectedRowKeys.has(getRowKey(row)))
      : []
  ), [getRowKey, rows, selectedRowKeys]);
  const selectedCount = selectedRows.length;
  const selectablePageRows = React.useMemo(
    () => pageRows.filter(canSelectRow),
    [canSelectRow, pageRows],
  );
  const allSelectablePageRowsSelected = selectablePageRows.length > 0
    && selectablePageRows.every((row) => selectedRowKeys?.has(getRowKey(row)));
  const someSelectablePageRowsSelected = selectablePageRows.some((row) => selectedRowKeys?.has(getRowKey(row)));
  const pageStartLabel = rows.length ? pageStartIndex + 1 : 0;
  const pageEndLabel = Math.min(rows.length, pageStartIndex + pageRows.length);
  const rowCountLabel = pageCount > 1
    ? `${pageStartLabel}-${pageEndLabel} of ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`
    : totalLabel;
  const clearSelection = React.useCallback(() => {
    onSelectedRowKeysChange?.(new Set());
  }, [onSelectedRowKeysChange]);

  React.useEffect(() => {
    const previousInputs = previousPaginationInputs.current;
    if (previousInputs.pageSize !== pageSize || previousInputs.rows !== rows) {
      previousPaginationInputs.current = { pageSize, rows };
      setPageIndex(0);
    }
  }, [pageSize, rows]);

  React.useEffect(() => {
    setPageIndex((current) => Math.min(current, lastPageIndex));
  }, [lastPageIndex]);

  React.useEffect(() => {
    if (!selectedRowKeys || !onSelectedRowKeysChange) return;
    const availableKeys = new Set(rows.map(getRowKey));
    const nextKeys = new Set(Array.from(selectedRowKeys).filter((key) => availableKeys.has(key)));
    if (nextKeys.size !== selectedRowKeys.size) {
      onSelectedRowKeysChange(nextKeys);
    }
  }, [getRowKey, onSelectedRowKeysChange, rows, selectedRowKeys]);

  const toggleRowSelection = (row: T, checked: boolean) => {
    if (!selectedRowKeys || !onSelectedRowKeysChange) return;
    const rowKey = getRowKey(row);
    const nextKeys = new Set(selectedRowKeys);
    if (checked) {
      nextKeys.add(rowKey);
    } else {
      nextKeys.delete(rowKey);
    }
    onSelectedRowKeysChange(nextKeys);
  };

  const togglePageSelection = (checked: boolean) => {
    if (!selectedRowKeys || !onSelectedRowKeysChange) return;
    const nextKeys = new Set(selectedRowKeys);
    for (const row of selectablePageRows) {
      const rowKey = getRowKey(row);
      if (checked) {
        nextKeys.add(rowKey);
      } else {
        nextKeys.delete(rowKey);
      }
    }
    onSelectedRowKeysChange(nextKeys);
  };

  return (
    <div>
      <div
        className="max-w-full rounded-md border transition-all duration-150"
        style={{ width: tableWidth }}
      >
        <Table className="table-fixed" style={{ minWidth: tableMinWidth }}>
          <colgroup>
            {selectionEnabled ? <col style={{ width: '48px' }} /> : null}
            {columns.map((column) => (
              <col
                key={column.key}
                style={column.width ? { width: column.width } : undefined}
              />
            ))}
          </colgroup>
          <TableHeader>
            <TableRow>
              {selectionEnabled ? (
                <TableHead className="w-12">
                  <div className="flex justify-center">
                    <Checkbox
                      aria-label="Select all rows on this page"
                      checked={allSelectablePageRowsSelected ? true : someSelectablePageRowsSelected ? 'indeterminate' : false}
                      disabled={!selectablePageRows.length}
                      onCheckedChange={(checked) => togglePageSelection(checked === true)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </div>
                </TableHead>
              ) : null}
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={column.headerClassName}
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.length ? (
              pageRows.map((row) => {
                const rowKey = getRowKey(row);
                const rowSelectable = canSelectRow(row);
                return (
                  <TableRow
                    key={rowKey}
                    className={cn('group', onRowClick && 'cursor-pointer hover:bg-gray-50')}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {selectionEnabled ? (
                      <TableCell className="w-12">
                        <div className="flex justify-center" onClick={(event) => event.stopPropagation()}>
                          <Checkbox
                            aria-label="Select row"
                            checked={selectedRowKeys?.has(rowKey) ?? false}
                            disabled={!rowSelectable}
                            onCheckedChange={(checked) => toggleRowSelection(row, checked === true)}
                          />
                        </div>
                      </TableCell>
                    ) : null}
                    {columns.map((column) => (
                      <TableCell key={column.key} className={column.cellClassName}>
                        {column.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + (selectionEnabled ? 1 : 0)} className="h-48 text-center">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-500">
                      {emptyIcon}
                    </div>
                    <p className="text-sm font-medium text-slate-800">{emptyTitle}</p>
                    {emptyAction}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className={cn(
        'mt-2 flex flex-wrap items-center justify-between gap-3 px-2 pb-2 text-sm text-muted-foreground',
        selectedCount > 0 && selectionEnabled && 'mb-16',
      )}>
        <div>
          {selectedCount > 0 && selectionEnabled ? (
            <>
              {selectedCount} of {rows.length} {rows.length === 1 ? 'row' : 'rows'} selected
            </>
          ) : (
            rowCountLabel
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {pageCount > 1 ? (
            <div className="flex items-center gap-6 lg:gap-8">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-slate-700">Rows per page</p>
                <Select
                  value={`${pageSize}`}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                  }}
                >
                  <SelectTrigger className="h-8 w-[74px]">
                    <SelectValue placeholder={`${pageSize}`} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {pageSizeOptions.map((option) => (
                      <SelectItem key={option} value={`${option}`}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex w-[100px] items-center justify-center text-sm font-medium text-slate-700">
                Page {pageIndex + 1} of {pageCount}
              </div>
              <div className="flex items-center gap-2">
                <TableIconButton
                  variant="outline"
                  size="sm"
                  className="hidden h-8 w-8 p-0 lg:flex"
                  onClick={() => setPageIndex(firstPageIndex)}
                  disabled={pageIndex === firstPageIndex}
                >
                  <span className="sr-only">Go to first page</span>
                  <ChevronsLeft className="h-4 w-4" />
                </TableIconButton>
                <TableIconButton
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setPageIndex((current) => Math.max(firstPageIndex, current - 1))}
                  disabled={pageIndex === firstPageIndex}
                >
                  <span className="sr-only">Go to previous page</span>
                  <ChevronLeft className="h-4 w-4" />
                </TableIconButton>
                <TableIconButton
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setPageIndex((current) => Math.min(lastPageIndex, current + 1))}
                  disabled={pageIndex === lastPageIndex}
                >
                  <span className="sr-only">Go to next page</span>
                  <ChevronRight className="h-4 w-4" />
                </TableIconButton>
                <TableIconButton
                  variant="outline"
                  size="sm"
                  className="hidden h-8 w-8 p-0 lg:flex"
                  onClick={() => setPageIndex(lastPageIndex)}
                  disabled={pageIndex === lastPageIndex}
                >
                  <span className="sr-only">Go to last page</span>
                  <ChevronsRight className="h-4 w-4" />
                </TableIconButton>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {selectionEnabled && renderBulkActions ? (
        <BulkActionBar open={selectedCount > 0}>
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <div className="text-sm font-medium text-stone-800">
              {selectedCount} of {rows.length} {rows.length === 1 ? 'row' : 'rows'} selected
            </div>
            <div className="flex items-center gap-2">
              {renderBulkActions({
                clearSelection,
                selectedCount,
                selectedRows,
              })}
            </div>
          </div>
        </BulkActionBar>
      ) : null}
    </div>
  );
};

const Drafting = () => {
  const { selectedCase } = useCaseContext();
  const { slug } = useParams({ from: '/matter/$slug/drafting' });
  const navigate = useNavigate();
  const { isEnabled: draftingEnabled } = useFeatures(FEATURE_FLAGS.DRAFTING);
  const dateLocale = useMatterLocale();
  const { user: sessionUser } = useAuth();
  const currentUser = sessionUser?.dbUser ?? sessionUser ?? null;
  const enabledMatterGuid = draftingEnabled ? slug : undefined;
  const { matter } = useMatter(enabledMatterGuid);
  const { draftDocuments, isLoading, mutate: mutateDraftDocuments } = useDraftDocuments(enabledMatterGuid);
  const [activeTab, setActiveTab] = React.useState<DraftWorkspaceTab>('drafts');
  const draftWorkspaceIdBase = React.useId();
  const draftSearchInputId = React.useId();
  const [draftQuery, setDraftQuery] = React.useState('');
  const [draftOwnerFilter, setDraftOwnerFilter] = React.useState<string[]>([]);
  const [draftFileTypeFilter, setDraftFileTypeFilter] = React.useState<string[]>([]);
  const [templateQuery, setTemplateQuery] = React.useState('');
  const [templateScopeFilter, setTemplateScopeFilter] = React.useState<TemplateScopeFilter>('all');
  const {
    templates,
    isLoading: isLoadingTemplates,
    mutate: mutateTemplates,
  } = useDraftDocumentTemplates(enabledMatterGuid, {
    query: templateQuery,
    limit: DRAFT_WORKSPACE_DEFAULT_PAGE_SIZE,
    scopes: templateScopeFilter === 'all' ? undefined : [templateScopeFilter],
  });
  const [newDraftDialogOpen, setNewDraftDialogOpen] = React.useState(false);
  const [newDraftInitialTemplateGuid, setNewDraftInitialTemplateGuid] = React.useState<string | null>(null);
  const [newDraftInitialTemplate, setNewDraftInitialTemplate] = React.useState<DraftDocumentTemplate | null>(null);
  const [templateUploadDialogOpen, setTemplateUploadDialogOpen] = React.useState(false);
  const [templateToEdit, setTemplateToEdit] = React.useState<DraftDocumentTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = React.useState<DraftDocumentTemplate | null>(null);
  const [downloadingGuid, setDownloadingGuid] = React.useState<string | null>(null);
  const [downloadingTemplateGuid, setDownloadingTemplateGuid] = React.useState<string | null>(null);
  const [draftToDelete, setDraftToDelete] = React.useState<DraftDocument | null>(null);
  const [draftsToBulkDelete, setDraftsToBulkDelete] = React.useState<DraftDocument[]>([]);
  const [deletingGuid, setDeletingGuid] = React.useState<string | null>(null);
  const [bulkDeletingDrafts, setBulkDeletingDrafts] = React.useState(false);
  const [deletingTemplateGuid, setDeletingTemplateGuid] = React.useState<string | null>(null);
  const [bulkDeletingTemplates, setBulkDeletingTemplates] = React.useState(false);
  const [templatesToBulkDelete, setTemplatesToBulkDelete] = React.useState<DraftDocumentTemplate[]>([]);
  const [selectedDraftKeys, setSelectedDraftKeys] = React.useState<Set<string>>(() => new Set());
  const [selectedTemplateKeys, setSelectedTemplateKeys] = React.useState<Set<string>>(() => new Set());
  const [updatingVisibilityGuid, setUpdatingVisibilityGuid] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<DraftSort>({ field: 'updated', direction: 'desc' });
  const canUploadMatterTemplates = matter?.role === 'Owner';
  const wordDraftDocuments = React.useMemo(
    () => draftDocuments.filter((draftDocument) => draftDocument.is_docx),
    [draftDocuments],
  );
  const searchFilteredDraftDocuments = React.useMemo(() => {
    const needle = draftQuery.trim().toLowerCase();
    return wordDraftDocuments.filter((draftDocument) => {
      if (!needle) return true;
      return draftSearchText(draftDocument).includes(needle);
    });
  }, [wordDraftDocuments, draftQuery]);
  const draftFileTypesForRows = React.useCallback((rows: DraftDocument[]) => (
    Array.from(new Set(rows.map(draftDocumentFileType))).sort((a, b) => a.localeCompare(b))
  ), []);
  const draftOwnerOptionSource = React.useMemo(() => (
    draftFileTypeFilter.length
      ? searchFilteredDraftDocuments.filter((draftDocument) => (
        draftFileTypeFilter.includes(draftDocumentFileType(draftDocument))
      ))
      : searchFilteredDraftDocuments
  ), [draftFileTypeFilter, searchFilteredDraftDocuments]);
  const draftOwnerOptions = React.useMemo(
    () => draftOwnerFacetOptionsForDrafts(draftOwnerOptionSource),
    [draftOwnerOptionSource],
  );
  const draftDocumentsBeforeFileTypeFilter = React.useMemo(() => (
    draftOwnerFilter.length
      ? searchFilteredDraftDocuments.filter((draftDocument) => (
        draftOwnerFilter.includes(draftOwnerLabel(draftDocument))
      ))
      : searchFilteredDraftDocuments
  ), [draftOwnerFilter, searchFilteredDraftDocuments]);
  const draftFileTypeOptions = React.useMemo(() => (
    fileTypeFacetOptionsForValues(
      draftFileTypesForRows(draftDocumentsBeforeFileTypeFilter),
    )
  ), [draftDocumentsBeforeFileTypeFilter, draftFileTypesForRows]);
  const filteredDraftDocuments = React.useMemo(() => (
    draftFileTypeFilter.length
      ? draftDocumentsBeforeFileTypeFilter.filter((draftDocument) => (
        draftFileTypeFilter.includes(draftDocumentFileType(draftDocument))
      ))
      : draftDocumentsBeforeFileTypeFilter
  ), [draftDocumentsBeforeFileTypeFilter, draftFileTypeFilter]);
  const sortedDraftDocuments = React.useMemo(
    () => sortDrafts(filteredDraftDocuments, sort),
    [filteredDraftDocuments, sort],
  );
  const hasDraftFilters = Boolean(draftQuery.trim()) || draftOwnerFilter.length > 0 || draftFileTypeFilter.length > 0;

  const setSortField = React.useCallback((field: DraftSortField) => {
    setSort((current) => {
      if (current.field === field) {
        return {
          field,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        field,
        direction: field === 'updated' ? 'desc' : 'asc',
      };
    });
  }, []);

  useShellHeader({
    header: 'Drafting',
  });

  const handleNewDraft = React.useCallback((template?: DraftDocumentTemplate | null) => {
    setNewDraftInitialTemplateGuid(template?.template_guid ?? null);
    setNewDraftInitialTemplate(template ?? null);
    setNewDraftDialogOpen(true);
  }, []);

  const handleDraftCreated = React.useCallback((draftDocument: DraftDocument) => {
    void mutateDraftDocuments();
    setActiveTab('drafts');
    toast.success('Draft created');
    void navigate({
      to: '/matter/$slug/drafting/$documentGuid',
      params: {
        slug,
        documentGuid: draftDocument.guid,
      },
    });
  }, [mutateDraftDocuments, navigate, slug]);

  const handleDownload = React.useCallback(async (draftDocument: DraftDocument) => {
    setDownloadingGuid(draftDocument.guid);
    try {
      await downloadDraftDocument(slug, draftDocument);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download draft document');
    } finally {
      setDownloadingGuid(null);
    }
  }, [slug]);

  const handleOpenDraft = React.useCallback((draftDocument: DraftDocument) => {
    if (!draftDocument.is_docx) return;
    void navigate({
      to: '/matter/$slug/drafting/$documentGuid',
      params: {
        slug,
        documentGuid: draftDocument.guid,
      },
    });
  }, [navigate, slug]);

  const handleOpenCaseFilesLocation = React.useCallback((draftDocument: DraftDocument) => {
    const folderGuid = draftDocument.file_reference?.folder_guid;
    if (!folderGuid) return;

    void navigate({
      to: '/matter/$slug/case-files/folder/$folderGuid',
      params: {
        slug,
        folderGuid,
      },
    });
  }, [navigate, slug]);

  const handleDelete = React.useCallback(async () => {
    if (!draftToDelete) return;
    const draft = draftToDelete;

    setDeletingGuid(draft.guid);
    try {
      await deleteDraftDocument(slug, draft.guid);
      await mutateDraftDocuments(
        (current) => current
          ? {
            ...current,
            documents: current.documents.filter((document) => document.guid !== draft.guid),
          }
          : current,
        { revalidate: false },
      );
      void mutateDraftDocuments();
      toast.success('Draft deleted');
      setDraftToDelete(null);
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to delete draft document');
    } finally {
      setDeletingGuid(null);
    }
  }, [draftToDelete, mutateDraftDocuments, slug]);

  const handleBulkDeleteDrafts = React.useCallback(async () => {
    const drafts = draftsToBulkDelete.filter((draft) => draft.can_delete);
    if (!drafts.length) return;

    setBulkDeletingDrafts(true);
    const deletedGuids = new Set<string>();
    const removeDeletedDraftsFromState = async () => {
      if (!deletedGuids.size) return;
      await mutateDraftDocuments(
        (current) => current
          ? {
            ...current,
            documents: current.documents.filter((document) => !deletedGuids.has(document.guid)),
          }
          : current,
        { revalidate: false },
      );
      setSelectedDraftKeys((current) => new Set(Array.from(current).filter((key) => !deletedGuids.has(key))));
      setDraftsToBulkDelete((current) => current.filter((draft) => !deletedGuids.has(draft.guid)));
    };
    try {
      for (const draft of drafts) {
        await deleteDraftDocument(slug, draft.guid);
        deletedGuids.add(draft.guid);
      }
      await removeDeletedDraftsFromState();
      void mutateDraftDocuments();
      toast.success(`${drafts.length} ${drafts.length === 1 ? 'draft' : 'drafts'} deleted`);
    } catch (error) {
      await removeDeletedDraftsFromState();
      void mutateDraftDocuments();
      const deletedCount = deletedGuids.size;
      toast.error(
        deletedCount
          ? `${deletedCount} ${deletedCount === 1 ? 'draft was' : 'drafts were'} deleted, but the remaining delete failed.`
          : apiErrorMessage(error) || 'Failed to delete selected drafts',
      );
    } finally {
      setBulkDeletingDrafts(false);
    }
  }, [draftsToBulkDelete, mutateDraftDocuments, slug]);

  const handleVisibilityChange = React.useCallback(async (
    draftDocument: DraftDocument,
    visibility: DraftDocumentVisibility,
  ) => {
    if (!draftDocument.can_update_visibility || draftDocument.visibility === visibility) return;
    setUpdatingVisibilityGuid(draftDocument.guid);
    try {
      const updatedDraft = await updateDraftDocumentVisibility(slug, draftDocument.guid, visibility);
      await mutateDraftDocuments(
        (current) => current
          ? {
            ...current,
            documents: current.documents.map((document) => (
              document.guid === updatedDraft.guid ? { ...document, ...updatedDraft } : document
            )),
          }
          : current,
        { revalidate: false },
      );
      toast.success(visibility === 'private' ? 'Draft is now private' : 'Draft is open to the matter');
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to update draft sharing');
    } finally {
      setUpdatingVisibilityGuid(null);
    }
  }, [mutateDraftDocuments, slug]);

  const handleTemplateUploaded = React.useCallback(async () => {
    await mutateTemplates();
    toast.success('Template uploaded');
  }, [mutateTemplates]);

  const handleTemplateUpdated = React.useCallback(async (updatedTemplate: DraftDocumentTemplate) => {
    await mutateTemplates(
      (current) => current
        ? {
          ...current,
          templates: current.templates.map((template) => (
            template.template_guid === updatedTemplate.template_guid ? updatedTemplate : template
          )),
        }
        : current,
      { revalidate: false },
    );
    void mutateTemplates();
    toast.success('Template updated');
    setTemplateToEdit(null);
  }, [mutateTemplates]);

  const handleTemplateConfigureRequest = React.useCallback((template: DraftDocumentTemplate) => {
    if (isTenantLevelTemplate(template)) {
      toast.error('Tenant templates can only be configured from Admin');
      return;
    }
    setTemplateToEdit(template);
  }, []);

  const handleTemplateDeleteRequest = React.useCallback((template: DraftDocumentTemplate) => {
    if (isTenantLevelTemplate(template)) {
      toast.error('Tenant templates can only be deleted from Admin');
      return;
    }
    setTemplateToDelete(template);
  }, []);

  const handleTemplateDownload = React.useCallback(async (template: DraftDocumentTemplate) => {
    setDownloadingTemplateGuid(template.template_guid);
    try {
      await downloadDraftDocumentTemplate(slug, template);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download template');
    } finally {
      setDownloadingTemplateGuid(null);
    }
  }, [slug]);

  const handleTemplateDelete = React.useCallback(async () => {
    if (!templateToDelete) return;
    const template = templateToDelete;

    if (isTenantLevelTemplate(template)) {
      toast.error('Tenant templates can only be deleted from Admin');
      setTemplateToDelete(null);
      return;
    }

    setDeletingTemplateGuid(template.template_guid);
    try {
      await deleteDraftDocumentTemplate(slug, template.template_guid);
      await mutateTemplates(
        (current) => current
          ? {
            ...current,
            templates: current.templates.filter((item) => item.template_guid !== template.template_guid),
          }
          : current,
        { revalidate: false },
      );
      void mutateTemplates();
      toast.success('Template deleted');
      setTemplateToDelete(null);
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to delete template');
    } finally {
      setDeletingTemplateGuid(null);
    }
  }, [mutateTemplates, slug, templateToDelete]);

  const handleBulkDeleteTemplates = React.useCallback(async () => {
    const templatesToDelete = templatesToBulkDelete.filter(canManageTemplateFromMatterView);
    if (!templatesToDelete.length) return;

    setBulkDeletingTemplates(true);
    const deletedTemplateGuids = new Set<string>();
    const removeDeletedTemplatesFromState = async () => {
      if (!deletedTemplateGuids.size) return;
      await mutateTemplates(
        (current) => current
          ? {
            ...current,
            templates: current.templates.filter((template) => !deletedTemplateGuids.has(template.template_guid)),
          }
          : current,
        { revalidate: false },
      );
      setSelectedTemplateKeys((current) => new Set(Array.from(current).filter((key) => !deletedTemplateGuids.has(key))));
      setTemplatesToBulkDelete((current) => (
        current.filter((template) => !deletedTemplateGuids.has(template.template_guid))
      ));
    };
    try {
      for (const template of templatesToDelete) {
        await deleteDraftDocumentTemplate(slug, template.template_guid);
        deletedTemplateGuids.add(template.template_guid);
      }
      await removeDeletedTemplatesFromState();
      void mutateTemplates();
      toast.success(`${templatesToDelete.length} ${templatesToDelete.length === 1 ? 'template' : 'templates'} deleted`);
    } catch (error) {
      await removeDeletedTemplatesFromState();
      void mutateTemplates();
      const deletedCount = deletedTemplateGuids.size;
      toast.error(
        deletedCount
          ? `${deletedCount} ${deletedCount === 1 ? 'template was' : 'templates were'} deleted, but the remaining delete failed.`
          : apiErrorMessage(error) || 'Failed to delete selected templates',
      );
    } finally {
      setBulkDeletingTemplates(false);
    }
  }, [mutateTemplates, slug, templatesToBulkDelete]);

  const draftsHeaderBelow = React.useMemo(() => (
    <HeaderTabs
      items={[
        { value: 'drafts', label: 'Drafts' },
        { value: 'templates', label: 'Templates' },
      ]}
      value={activeTab}
      onChange={(value) => setActiveTab(value as DraftWorkspaceTab)}
      ariaLabel="Drafting workspace"
      idBase={draftWorkspaceIdBase}
    />
  ), [activeTab, draftWorkspaceIdBase]);

  useShellHeaderBelow(selectedCase ? draftsHeaderBelow : null);

  if (!selectedCase) {
    return <NoCaseSelected />;
  }

  if (!draftingEnabled) {
    return (
      <div className="p-6">
        <PageError message="The drafting feature is not enabled for this tenant." />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50/40 px-6 py-6">
      {activeTab === 'templates' ? (
        <TemplatesPanel
          deletingTemplateGuid={deletingTemplateGuid}
          downloadingTemplateGuid={downloadingTemplateGuid}
          isLoading={isLoadingTemplates}
          matterGuid={slug}
          canUploadMatterTemplates={canUploadMatterTemplates}
          onCreateDraft={handleNewDraft}
          onDelete={handleTemplateDeleteRequest}
          onDownload={handleTemplateDownload}
          onEdit={handleTemplateConfigureRequest}
          onUpload={() => setTemplateUploadDialogOpen(true)}
          onBulkDelete={setTemplatesToBulkDelete}
          query={templateQuery}
          selectedTemplateKeys={selectedTemplateKeys}
          setSelectedTemplateKeys={setSelectedTemplateKeys}
          setQuery={setTemplateQuery}
          scopeFilter={templateScopeFilter}
          setScopeFilter={setTemplateScopeFilter}
          tabIdBase={draftWorkspaceIdBase}
          templates={templates}
          bulkDeletingTemplates={bulkDeletingTemplates}
        />
      ) : isLoading ? (
        <div className="flex h-56 items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div
          id={`${draftWorkspaceIdBase}-panel-drafts`}
          role="tabpanel"
          aria-labelledby={`${draftWorkspaceIdBase}-tab-drafts`}
          className="w-full"
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
              <div className="relative w-full max-w-md md:w-80">
                <label htmlFor={draftSearchInputId} className="sr-only">Search drafts</label>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id={draftSearchInputId}
                  value={draftQuery}
                  onChange={(event) => setDraftQuery(event.target.value)}
                  placeholder="Search drafts"
                  className="h-10 pl-9"
                />
              </div>
            </div>
            <Button
              icon={FilePlus2}
              text="New draft"
              className={CREATE_DRAFT_BUTTON_CLASS_NAME}
              onClick={() => handleNewDraft()}
            />
          </div>

          <DraftWorkspaceTable
            rows={sortedDraftDocuments}
            getRowKey={(draftDocument) => draftDocument.guid}
            getCanSelectRow={(draftDocument) => draftDocument.can_delete}
            onRowClick={handleOpenDraft}
            selectedRowKeys={selectedDraftKeys}
            onSelectedRowKeysChange={setSelectedDraftKeys}
            emptyIcon={wordDraftDocuments.length
              ? <Search className="h-5 w-5" />
              : <FileWordIcon className="h-5 w-5" />}
            emptyTitle={wordDraftDocuments.length ? 'No Word drafts found.' : 'No Word drafts yet.'}
            emptyAction={hasDraftFilters ? (
              <Button
                variant="outline"
                text="Clear filters"
                onClick={() => {
                  setDraftQuery('');
                  setDraftOwnerFilter([]);
                  setDraftFileTypeFilter([]);
                }}
              />
            ) : null}
            renderBulkActions={({ selectedRows }) => (
              <Button
                variant="destructive"
                size="sm"
                icon={Trash2}
                text="Delete"
                loading={bulkDeletingDrafts}
                loadingText="Deleting..."
                disabled={!!deletingGuid || bulkDeletingDrafts}
                onClick={() => setDraftsToBulkDelete(selectedRows)}
              />
            )}
            tableMinWidth="1048px"
            totalLabel={`${sortedDraftDocuments.length} ${sortedDraftDocuments.length === 1 ? 'row' : 'rows'} total`}
            columns={[
              {
                key: 'file-type',
                header: (
                  <DraftFileTypeHeader
                    options={draftFileTypeOptions}
                    selectedValues={draftFileTypeFilter}
                    onSelectedValuesChange={setDraftFileTypeFilter}
                  />
                ),
                width: '64px',
                headerClassName: 'w-16',
                cellClassName: 'w-16',
                render: (draftDocument) => (
                  <FileTypeCell
                    fileName={draftDocumentFileName(draftDocument)}
                    originalFileType={draftDocumentFileType(draftDocument)}
                    isDraft={false}
                    documentId={draftDocument.guid}
                  />
                ),
              },
              {
                key: 'document',
                header: (
                  <DraftSortHeader activeSort={sort} field="title" onClick={setSortField}>
                    Draft name
                  </DraftSortHeader>
                ),
                cellClassName: 'min-w-0',
                render: (draftDocument) => {
                  const title = draftDocument.title || 'Untitled draft';

                  return (
                    <div className="min-w-0" title={title}>
                      <span className="block truncate font-medium">
                        {title}
                      </span>
                      <span className="block truncate text-xs text-gray-500">
                        {draftSubtitle(draftDocument)}
                      </span>
                    </div>
                  );
                },
              },
              {
                key: 'owner',
                header: (
                  <DraftOwnerFilterHeader
                    options={draftOwnerOptions}
                    selectedValues={draftOwnerFilter}
                    onSelectedValuesChange={setDraftOwnerFilter}
                  />
                ),
                headerClassName: 'w-[140px]',
                cellClassName: 'w-[140px]',
                width: '140px',
                render: (draftDocument) => (
                  <p className="truncate">{draftOwnerLabel(draftDocument)}</p>
                ),
              },
              {
                key: 'sharing',
                header: 'Matter sharing',
                cellClassName: 'w-[180px]',
                width: '180px',
                render: (draftDocument) => (
                  <div
                    onClick={(event) => event.stopPropagation()}
                    onPointerDown={(event) => event.stopPropagation()}
                  >
                    <DraftVisibilityMenu
                      align="start"
                      canUpdateVisibility={Boolean(draftDocument.can_update_visibility)}
                      isUpdatingVisibility={updatingVisibilityGuid === draftDocument.guid}
                      onVisibilityChange={(visibility) => {
                        void handleVisibilityChange(draftDocument, visibility);
                      }}
                      showLabel
                      visibility={draftDocument.visibility}
                    />
                  </div>
                ),
              },
              {
                key: 'updated',
                header: (
                  <DraftSortHeader activeSort={sort} field="updated" onClick={setSortField}>
                    Updated
                  </DraftSortHeader>
                ),
                cellClassName: 'w-[180px]',
                width: '180px',
                render: (draftDocument) => (
                  <div>
                    <p className="truncate">{formatDateTime(draftUpdatedAt(draftDocument), dateLocale)}</p>
                    <p className="truncate text-xs text-gray-500">
                      Updated by {draftLastUpdatedByLabel(draftDocument, currentUser)}
                    </p>
                  </div>
                ),
              },
              {
                key: 'actions',
                header: <span className="sr-only">Actions</span>,
                headerClassName: 'w-16',
                cellClassName: 'w-16',
                width: '64px',
                render: (draftDocument) => (
                  <SimpleTableActionsDropdown
                    label={`Actions for ${draftDocument.title}`}
                    actions={[
                      ...(draftDocument.file_reference?.folder_guid
                        ? [{
                          label: 'Open case files location',
                          icon: FolderOpen,
                          onSelect: () => handleOpenCaseFilesLocation(draftDocument),
                        }]
                        : []),
                      {
                        label: 'Download',
                        icon: Download,
                        loading: downloadingGuid === draftDocument.guid,
                        loadingLabel: 'Downloading...',
                        onSelect: () => handleDownload(draftDocument),
                      },
                      {
                        label: 'Delete',
                        icon: Trash2,
                        destructive: true,
                        loading: deletingGuid === draftDocument.guid,
                        loadingLabel: 'Deleting...',
                        disabled: !!deletingGuid || bulkDeletingDrafts || !draftDocument.can_delete,
                        onSelect: () => setDraftToDelete(draftDocument),
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </div>
      )}
      {slug && newDraftDialogOpen ? (
        <NewDraftDialog
          matterGuid={slug}
          open={newDraftDialogOpen}
          onOpenChange={(nextOpen) => {
            setNewDraftDialogOpen(nextOpen);
            if (!nextOpen) {
              setNewDraftInitialTemplateGuid(null);
              setNewDraftInitialTemplate(null);
            }
          }}
          onCreated={handleDraftCreated}
          initialTemplateGuid={newDraftInitialTemplateGuid}
          initialTemplate={newDraftInitialTemplate}
        />
      ) : null}
      {slug ? (
        <TemplateUploadDialog
          matterGuid={slug}
          open={templateUploadDialogOpen}
          onOpenChange={setTemplateUploadDialogOpen}
          onUploaded={handleTemplateUploaded}
        />
      ) : null}
      {slug && templateToEdit ? (
        <TemplateEditDialog
          matterGuid={slug}
          open={!!templateToEdit}
          template={templateToEdit}
          onOpenChange={(open) => {
            if (!open) setTemplateToEdit(null);
          }}
          onUpdated={handleTemplateUpdated}
        />
      ) : null}
      <AlertDialog open={!!draftToDelete} onOpenChange={(open) => {
        if (!open) setDraftToDelete(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete &quot;{draftToDelete?.title}&quot; from Drafting and case files.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingGuid}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={!draftToDelete || deletingGuid === draftToDelete.guid}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {deletingGuid === draftToDelete?.guid ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={draftsToBulkDelete.length > 0} onOpenChange={(open) => {
        if (!open && !bulkDeletingDrafts) setDraftsToBulkDelete([]);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete selected drafts?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete {draftsToBulkDelete.length} selected {draftsToBulkDelete.length === 1 ? 'draft' : 'drafts'} from Drafting and case files.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeletingDrafts}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDeleteDrafts}
              disabled={!draftsToBulkDelete.length || bulkDeletingDrafts}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {bulkDeletingDrafts ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!templateToDelete} onOpenChange={(open) => {
        if (!open) setTemplateToDelete(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove &quot;{templateToDelete?.title}&quot; from the template list. Existing drafts created from it are not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingTemplateGuid}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTemplateDelete}
              disabled={!templateToDelete || deletingTemplateGuid === templateToDelete.template_guid}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {deletingTemplateGuid === templateToDelete?.template_guid ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={templatesToBulkDelete.length > 0} onOpenChange={(open) => {
        if (!open && !bulkDeletingTemplates) setTemplatesToBulkDelete([]);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete selected templates?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {templatesToBulkDelete.length} selected {templatesToBulkDelete.length === 1 ? 'template' : 'templates'} from the template list. Existing drafts created from them are not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeletingTemplates}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDeleteTemplates}
              disabled={!templatesToBulkDelete.length || bulkDeletingTemplates}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {bulkDeletingTemplates ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const TemplatesPanel = ({
  bulkDeletingTemplates,
  deletingTemplateGuid,
  downloadingTemplateGuid,
  isLoading,
  matterGuid,
  canUploadMatterTemplates,
  onCreateDraft,
  onDelete,
  onDownload,
  onEdit,
  onBulkDelete,
  onUpload,
  query,
  selectedTemplateKeys,
  setSelectedTemplateKeys,
  setQuery,
  scopeFilter,
  setScopeFilter,
  tabIdBase,
  templates,
}: {
  bulkDeletingTemplates: boolean;
  deletingTemplateGuid: string | null;
  downloadingTemplateGuid: string | null;
  isLoading: boolean;
  matterGuid: string;
  canUploadMatterTemplates: boolean;
  onCreateDraft: (template: DraftDocumentTemplate) => void;
  onDelete: (template: DraftDocumentTemplate) => void;
  onDownload: (template: DraftDocumentTemplate) => void;
  onEdit: (template: DraftDocumentTemplate) => void;
  onBulkDelete: (templates: DraftDocumentTemplate[]) => void;
  onUpload: () => void;
  query: string;
  selectedTemplateKeys: Set<string>;
  setSelectedTemplateKeys: (keys: Set<string>) => void;
  setQuery: (query: string) => void;
  scopeFilter: TemplateScopeFilter;
  setScopeFilter: (scope: TemplateScopeFilter) => void;
  tabIdBase: string;
  templates: DraftDocumentTemplate[];
}) => {
  const searchInputId = React.useId();
  const [fileTypeFilter, setFileTypeFilter] = React.useState<string[]>([]);
  const { openPanel } = useSidePanel();
  const fileTypeOptions = React.useMemo(() => (
    fileTypeFacetOptionsForValues(templates.length ? [DRAFT_TEMPLATE_FILE_TYPE] : [])
  ), [templates]);
  const scopeFilterOptions = React.useMemo(() => (
    templateScopeFacetOptionsForTemplates(templates, scopeFilter)
  ), [scopeFilter, templates]);
  const visibleTemplates = React.useMemo(() => (
    fileTypeFilter.length && !fileTypeFilter.includes(DRAFT_TEMPLATE_FILE_TYPE)
      ? []
      : templates
  ), [fileTypeFilter, templates]);
  const openTemplatePreview = React.useCallback((template: DraftDocumentTemplate) => {
    openPanel([
      {
        id: 'draft-template-preview',
        label: 'Template',
        icon: FileText,
        content: (
          <TemplatePreviewContent
            matterGuid={matterGuid}
            onCreateDraft={onCreateDraft}
            onDelete={onDelete}
            onDownload={onDownload}
            onEdit={onEdit}
            template={template}
          />
        ),
      },
    ], 'draft-template-preview');
  }, [
    matterGuid,
    onCreateDraft,
    onDelete,
    onDownload,
    onEdit,
    openPanel,
  ]);

  return (
    <div
      id={`${tabIdBase}-panel-templates`}
      role="tabpanel"
      aria-labelledby={`${tabIdBase}-tab-templates`}
      className="w-full"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
          <div className="relative w-full max-w-md md:w-80">
            <label htmlFor={searchInputId} className="sr-only">Search templates</label>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id={searchInputId}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search templates"
              className="h-10 pl-9"
            />
          </div>
        </div>
        <Button
          icon={FilePlus2}
          text="New template"
          className={CREATE_DRAFT_BUTTON_CLASS_NAME}
          disabled={!canUploadMatterTemplates}
          tooltip={!canUploadMatterTemplates ? 'Only matter owners can upload matter templates' : undefined}
          onClick={onUpload}
        />
      </div>

      {isLoading ? (
        <div className="flex h-56 items-center justify-center">
          <Loading />
        </div>
      ) : (
        <DraftWorkspaceTable
          rows={visibleTemplates}
          getRowKey={(template) => template.template_guid}
          getCanSelectRow={canManageTemplateFromMatterView}
          onRowClick={openTemplatePreview}
          selectedRowKeys={selectedTemplateKeys}
          onSelectedRowKeysChange={setSelectedTemplateKeys}
          emptyIcon={<FileWordIcon className="h-5 w-5" />}
          emptyTitle="No templates found."
          renderBulkActions={({ selectedRows }) => (
            <Button
              variant="destructive"
              size="sm"
              icon={Trash2}
              text="Delete"
              loading={bulkDeletingTemplates}
              loadingText="Deleting..."
              disabled={!!deletingTemplateGuid || bulkDeletingTemplates}
              onClick={() => onBulkDelete(selectedRows)}
            />
          )}
          tableMinWidth="836px"
          totalLabel={`${visibleTemplates.length} ${visibleTemplates.length === 1 ? 'row' : 'rows'} total`}
          columns={[
            {
              key: 'file-type',
              header: (
                <DraftFileTypeHeader
                  options={fileTypeOptions}
                  selectedValues={fileTypeFilter}
                  onSelectedValuesChange={setFileTypeFilter}
                />
              ),
              width: '64px',
              headerClassName: 'w-16',
              cellClassName: 'w-16',
              render: (template) => (
                <FileTypeCell
                  fileName={`${template.title || 'template'}.docx`}
                  originalFileType="Word"
                  isDraft={false}
                  documentId={template.template_guid}
                />
              ),
            },
            {
              key: 'template',
              header: 'Template name',
              cellClassName: 'min-w-0',
              render: (template) => {
                const title = template.title || 'Untitled template';

                return (
                  <div className="min-w-0" title={title}>
                    <p className="truncate font-medium">
                      {title}
                    </p>
                    <p className="line-clamp-2 text-xs text-gray-500">
                      {templateDescription(template)}
                    </p>
                  </div>
                );
              },
            },
            {
              key: 'scope',
              header: (
                <TemplateScopeFilterHeader
                  options={scopeFilterOptions}
                  value={scopeFilter}
                  onChange={setScopeFilter}
                />
              ),
              cellClassName: 'w-[140px]',
              width: '140px',
              render: (template) => (
                <span>
                  {templateScopeLabel(template.scope)}
                </span>
              ),
            },
            {
              key: 'actions',
              header: <span className="sr-only">Actions</span>,
              headerClassName: 'w-16',
              cellClassName: 'w-16',
              width: '64px',
              render: (template) => {
                const canManageFromMatterView = canManageTemplateFromMatterView(template);

                return (
                  <SimpleTableActionsDropdown
                    label={`Actions for ${template.title}`}
                    actions={[
                      {
                        label: 'Create draft',
                        icon: Plus,
                        onSelect: () => onCreateDraft(template),
                      },
                      {
                        label: 'Download',
                        icon: Download,
                        loading: downloadingTemplateGuid === template.template_guid,
                        loadingLabel: 'Downloading...',
                        onSelect: () => onDownload(template),
                      },
                      {
                        label: 'Configure template',
                        icon: Settings,
                        disabled: !canManageFromMatterView,
                        onSelect: () => onEdit(template),
                      },
                      {
                        label: 'Delete',
                        icon: Trash2,
                        destructive: true,
                        disabled: !canManageFromMatterView || !!deletingTemplateGuid || bulkDeletingTemplates,
                        loading: deletingTemplateGuid === template.template_guid,
                        loadingLabel: 'Deleting...',
                        onSelect: () => onDelete(template),
                      },
                    ]}
                  />
                );
              },
            },
          ]}
        />
      )}
    </div>
  );
};

const TemplatePreviewContent = ({
  matterGuid,
  onCreateDraft,
  onDelete,
  onDownload,
  onEdit,
  template,
}: {
  matterGuid: string;
  onCreateDraft: (template: DraftDocumentTemplate) => void;
  onDelete: (template: DraftDocumentTemplate) => void;
  onDownload: (template: DraftDocumentTemplate) => void | Promise<void>;
  onEdit: (template: DraftDocumentTemplate) => void;
  template: DraftDocumentTemplate;
}) => {
  const {
    documentBuffer,
    isError,
    isLoading,
  } = useDraftDocumentTemplateDocx(matterGuid, template.template_guid);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const description = templateDescription(template);
  const matcherPrompt = template.when_to_use?.trim();
  const canManageFromMatterView = canManageTemplateFromMatterView(template);
  const handleDownload = React.useCallback(async () => {
    setIsDownloading(true);
    try {
      await onDownload(template);
    } finally {
      setIsDownloading(false);
    }
  }, [onDownload, template]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="shrink-0 border-b border-slate-200/70 bg-white px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {templateScopeLabel(template.scope)}
              </span>
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                {DRAFT_TEMPLATE_FORMAT}
              </span>
            </div>
            <h2 className="mt-2 line-clamp-2 text-base font-semibold leading-6 text-slate-950">
              {template.title}
            </h2>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              icon={Plus}
              text="Draft"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 border-slate-200 bg-white text-slate-700 shadow-none hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 active:bg-slate-100"
              onClick={() => onCreateDraft(template)}
            />
            <Button
              variant="outline"
              size="sm"
              icon={Download}
              loading={isDownloading}
              tooltip="Download template"
              aria-label={`Download ${template.title}`}
              className="h-8 w-8 rounded-md border-slate-200 bg-white text-slate-600 shadow-xs hover:bg-slate-50 hover:text-slate-950"
              onClick={() => {
                void handleDownload();
              }}
            />
            <Button
              variant="outline"
              size="sm"
              icon={Settings}
              disabled={!canManageFromMatterView}
              tooltip={
                canManageFromMatterView
                  ? 'Configure template'
                  : unavailableMatterTemplateActionTooltip(template, 'configure')
              }
              aria-label={`Configure ${template.title}`}
              className="h-8 w-8 rounded-md border-slate-200 bg-white text-slate-600 shadow-xs hover:bg-slate-50 hover:text-slate-950"
              onClick={() => onEdit(template)}
            />
            <Button
              variant="outline"
              size="sm"
              icon={Trash2}
              disabled={!canManageFromMatterView}
              tooltip={
                canManageFromMatterView
                  ? 'Delete template'
                  : unavailableMatterTemplateActionTooltip(template, 'delete')
              }
              aria-label={`Delete ${template.title}`}
              className="h-8 w-8 rounded-md border-slate-200 bg-white text-slate-500 shadow-xs hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              onClick={() => onDelete(template)}
            />
          </div>
        </div>

        <p className="mt-3 line-clamp-2 text-sm leading-6 text-slate-600">
          {description}
        </p>
        {matcherPrompt ? (
          <div className="mt-3">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Matcher</p>
            <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-600">
              {matcherPrompt}
            </p>
          </div>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 bg-slate-50">
        <DraftDocxViewer
          buffer={documentBuffer}
          documentId={`template-${template.template_guid}`}
          emptyClassName="flex h-full min-h-[320px] items-center justify-center bg-slate-50/80 px-6 text-center text-sm text-slate-500"
          error={isError}
          fitToWidth
          showZoomControls
          frameClassName="draft-docx-editor h-full min-h-0 overflow-auto bg-slate-50"
          isLoading={isLoading}
          placeholderClassName="h-full min-h-0"
          portalZIndex={40}
          title={template.title}
          versionKey={template.template_guid}
        />
      </div>
    </div>
  );
};

const TemplateUploadDialog = ({
  matterGuid,
  onOpenChange,
  onUploaded,
  open,
}: {
  matterGuid: string;
  onOpenChange: (open: boolean) => void;
  onUploaded: () => Promise<void>;
  open: boolean;
}) => {
  const fileInputId = React.useId();
  const titleInputId = React.useId();
  const descriptionInputId = React.useId();
  const whenToUseInputId = React.useId();
  const [file, setFile] = React.useState<File | null>(null);
  const [title, setTitle] = React.useState('');
  const [titleTouched, setTitleTouched] = React.useState(false);
  const [description, setDescription] = React.useState('');
  const [whenToUse, setWhenToUse] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) return;
    setFile(null);
    setTitle('');
    setTitleTouched(false);
    setDescription('');
    setWhenToUse('');
    setIsSaving(false);
  }, [open]);

  const handleFileChange = React.useCallback((nextFile: File | null) => {
    setFile(nextFile);
    if (nextFile && !titleTouched) {
      setTitle(titleFromTemplateFile(nextFile.name));
    }
  }, [titleTouched]);

  const handleSubmit = React.useCallback(async () => {
    if (!file) {
      toast.error('Choose a DOCX template file');
      return;
    }
    if (!isTemplateMetadataValid(description, whenToUse)) {
      toast.error('Add a description and a matcher prompt of at least 50 characters');
      return;
    }

    const cleanTitle = title.trim() || titleFromTemplateFile(file.name) || 'Untitled template';
    setIsSaving(true);
    try {
      await createDraftDocumentTemplate(matterGuid, {
        scope: 'matter',
        title: cleanTitle,
        file,
        description: description.trim() || null,
        when_to_use: whenToUse.trim() || null,
      });
      await onUploaded();
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to upload template');
    } finally {
      setIsSaving(false);
    }
  }, [description, file, matterGuid, onOpenChange, onUploaded, title, whenToUse]);

  const canSubmit = !!file && isTemplateMetadataValid(description, whenToUse);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload template</DialogTitle>
          <DialogDescription>
            Add a reusable DOCX template for this matter.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <label htmlFor={fileInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
              File
            </label>
            <TemplateUploadDropArea
              inputId={fileInputId}
              file={file}
              onFileChange={handleFileChange}
              disabled={isSaving}
            />
          </div>
          <div className="grid gap-2">
            <label htmlFor={titleInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Title
            </label>
            <Input
              id={titleInputId}
              value={title}
              onChange={(event) => {
                setTitleTouched(true);
                setTitle(event.target.value);
              }}
              placeholder="Template title"
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor={descriptionInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Description
              </label>
              <span className="text-xs text-slate-500">
                {templateDescriptionLength(description)}/{TEMPLATE_DESCRIPTION_MAX_LENGTH}
              </span>
            </div>
            <Textarea
              id={descriptionInputId}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={TEMPLATE_DESCRIPTION_MAX_LENGTH}
              required
              className="min-h-20 text-sm"
              placeholder="Short description"
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor={whenToUseInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Matcher prompt
              </label>
              <span className="text-xs text-slate-500">
                {templateMatcherPromptLength(whenToUse)}/{TEMPLATE_MATCHER_PROMPT_MIN_LENGTH} min
              </span>
            </div>
            <Textarea
              id={whenToUseInputId}
              value={whenToUse}
              onChange={(event) => setWhenToUse(event.target.value)}
              minLength={TEMPLATE_MATCHER_PROMPT_MIN_LENGTH}
              required
              className="min-h-20 text-sm"
              placeholder="How Case Partner should decide when to use this template"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            text="Cancel"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          />
          <Button
            text="Upload"
            loadingText="Uploading"
            loading={isSaving}
            disabled={!canSubmit}
            onClick={handleSubmit}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TemplateEditDialog = ({
  matterGuid,
  onOpenChange,
  onUpdated,
  open,
  template,
}: {
  matterGuid: string;
  onOpenChange: (open: boolean) => void;
  onUpdated: (template: DraftDocumentTemplate) => Promise<void>;
  open: boolean;
  template: DraftDocumentTemplate;
}) => {
  const titleInputId = React.useId();
  const descriptionInputId = React.useId();
  const whenToUseInputId = React.useId();
  const [title, setTitle] = React.useState(template.title);
  const [description, setDescription] = React.useState(template.description ?? '');
  const [whenToUse, setWhenToUse] = React.useState(template.when_to_use ?? '');
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTitle(template.title);
    setDescription(template.description ?? '');
    setWhenToUse(template.when_to_use ?? '');
    setIsSaving(false);
  }, [open, template]);

  const handleSubmit = React.useCallback(async () => {
    if (!title.trim()) return;
    if (!isTemplateMetadataValid(description, whenToUse)) {
      toast.error('Add a description and a matcher prompt of at least 50 characters');
      return;
    }
    setIsSaving(true);
    try {
      const updated = await updateDraftDocumentTemplate(
        matterGuid,
        template.template_guid,
        {
          title: title.trim(),
          description: description.trim() || null,
          when_to_use: whenToUse.trim() || null,
        },
      );
      await onUpdated(updated);
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to update template');
    } finally {
      setIsSaving(false);
    }
  }, [description, matterGuid, onOpenChange, onUpdated, template.template_guid, title, whenToUse]);

  const canSubmit = !!title.trim() && isTemplateMetadataValid(description, whenToUse);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure template</DialogTitle>
          <DialogDescription>
            Update how this template appears to users and Case Partner.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <label htmlFor={titleInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Title
            </label>
            <Input
              id={titleInputId}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Template title"
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor={descriptionInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Description
              </label>
              <span className="text-xs text-slate-500">
                {templateDescriptionLength(description)}/{TEMPLATE_DESCRIPTION_MAX_LENGTH}
              </span>
            </div>
            <Textarea
              id={descriptionInputId}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={TEMPLATE_DESCRIPTION_MAX_LENGTH}
              required
              className="min-h-20 text-sm"
              placeholder="Short description"
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor={whenToUseInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Matcher prompt
              </label>
              <span className="text-xs text-slate-500">
                {templateMatcherPromptLength(whenToUse)}/{TEMPLATE_MATCHER_PROMPT_MIN_LENGTH} min
              </span>
            </div>
            <Textarea
              id={whenToUseInputId}
              value={whenToUse}
              onChange={(event) => setWhenToUse(event.target.value)}
              minLength={TEMPLATE_MATCHER_PROMPT_MIN_LENGTH}
              required
              className="min-h-20 text-sm"
              placeholder="How Case Partner should decide when to use this template"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            text="Cancel"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          />
          <Button
            text="Save"
            loadingText="Saving"
            loading={isSaving}
            disabled={!canSubmit}
            onClick={handleSubmit}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Drafting;
